import { useMemo, useRef, useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useCollectionBinding, useTokens } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { contrastRatio, pickHighestContrast, relativeLuminance } from '../utils/luminance';

// ─── Special offers ─────────────────────────────────────────────────────────
// This section links to the property's live `special_offers` collection. When
// an offer is currently active (has content and falls inside its start/end date
// window) we surface the offer's title in the subtitle line and its description
// in the body copy; otherwise both fall back to their generic, editable tokens.
// Same active-window rules as the SpecialOfferBanner so the two components agree
// on what "active" means.

interface SpecialOffer {
  title?: string | null;
  description?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  [key: string]: unknown;
}

function hasValue(v: unknown): v is string {
  if (v == null) return false;
  const s = String(v).trim().toLowerCase();
  return s !== '' && s !== '-' && s !== 'null' && s !== 'undefined' && s !== 'false';
}

// Offer descriptions are stored as richtext; render them as plain text here so
// the body copy stays a single clean paragraph. Regex-based (not DOM-based) so
// it produces identical output during SSG and client hydration.
function stripHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function isOfferActive(offer: SpecialOffer | null | undefined): boolean {
  if (!offer) return false;
  // `enabled` is a static always-false default in the data, so we ignore it —
  // visibility is driven by content presence + the start/end date window.
  if (!hasValue(offer.title) && !hasValue(offer.description)) return false;

  const now = Date.now();
  if (hasValue(offer.start_date)) {
    const start = new Date(offer.start_date!).getTime();
    if (!isNaN(start) && start > now) return false;
  }
  if (hasValue(offer.end_date)) {
    const end = new Date(offer.end_date!).getTime();
    if (!isNaN(end) && end < now) return false;
  }
  return true;
}

// ─── Slot group definitions ───────────────────────────────────────────────────

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const ctaDef: SlotGroupDefinition = {
  label: 'CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/contact' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Contact Us' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

export function PinnedCallouts() {
  const bg = useSlotGroup('pinnedcallouts.bg_image', bgImageDef);
  const cta = useSlotGroup('pinnedcallouts.cta', ctaDef);

  const offers = useCollectionBinding<SpecialOffer>('pinnedcallouts.offers', {
    collection: 'special_offers',
  });
  const { offerTitle, offerDescription } = useMemo(() => {
    const active = (offers || []).find(isOfferActive);
    return {
      offerTitle: active && hasValue(active.title) ? active.title! : null,
      offerDescription:
        active && hasValue(active.description) ? stripHtml(active.description!) : null,
    };
  }, [offers]);
  const hasActiveOffer = Boolean(offerTitle || offerDescription);

  // Fallback title color — the fallback heading normally paints in the theme
  // `primary`, but it sits on the parallax photo under a 55% black overlay, so
  // a dark primary (deep plum, navy, etc.) can wash out against it. Keep
  // primary when it clears WCAG AA on that dark surface; otherwise flip to
  // white so the heading always reads. Modeled against a neutral mid-tone photo
  // darkened by the overlay so the choice doesn't depend on the live image.
  const tokens = useTokens();
  const fallbackTitleColor = useMemo(() => {
    const primary = tokens?.color?.primary;
    if (!primary) return null; // no token → keep the CSS var default
    const WHITE = '#ffffff';
    // A black / near-black primary would vanish into the dark overlay, so force
    // white regardless of the surface math below.
    if (relativeLuminance(primary) <= 0.02) return WHITE;
    const OVERLAY_BLACK = 0.55;
    const MID_TONE = 128;
    const s = Math.round(MID_TONE * (1 - OVERLAY_BLACK));
    const surface = `rgb(${s}, ${s}, ${s})`;
    if (contrastRatio(surface, primary) >= 4.5) return primary;
    return pickHighestContrast(surface, [primary, WHITE]) ?? WHITE;
  }, [tokens]);

  useRevealStyles();
  const contentRef = useScrollReveal<HTMLDivElement>();

  // Parallax: section bounds reference + the element we translate
  const sectionRef = useRef<HTMLElement>(null);
  const parallaxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const parallax = parallaxRef.current;
    if (!section || !parallax) return;

    let rafId = 0;
    let active = false;

    const tick = () => {
      const rect = section.getBoundingClientRect();
      const vh = window.innerHeight;
      // progress: 0 = section enters from bottom, 0.5 = centred, 1 = exits at top
      const progress = (vh - rect.top) / (vh + rect.height);
      const clamped = Math.max(0, Math.min(1, progress));
      // Background travels 80 px total (±40 px) — slower than page scroll
      const offset = (clamped - 0.5) * 80;
      parallax.style.transform = `translateY(${offset}px)`;
      if (active) rafId = requestAnimationFrame(tick);
    };

    // IntersectionObserver works regardless of whether the scroll container is
    // window, an inner div, or an editor iframe — window.scroll would miss those.
    // rootMargin pre-activates 200 px before the section enters so there is no
    // visible jump when it first scrolls into view.
    const observer = new IntersectionObserver(
      ([entry]) => {
        active = entry.isIntersecting;
        if (active) rafId = requestAnimationFrame(tick);
        else cancelAnimationFrame(rafId);
      },
      { threshold: 0, rootMargin: '200px' },
    );

    observer.observe(section);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden min-h-[320px] md:min-h-[360px] flex items-center"
    >
      {/* ── Parallax background (z-0) ── */}
      {/*
        parallaxRef is intentionally OUTSIDE EditableSlotGroup so the runtime
        wrapper never becomes its containing block.  The div is 140 % tall
        (top/bottom: -20 %) so the ±40 px rAF translation never exposes bare
        edges; overflow-hidden on the section keeps everything contained.
      */}
      <div
        ref={parallaxRef}
        className="absolute left-0 right-0 z-0"
        style={{ top: '-20%', bottom: '-20%', willChange: 'transform' }}
      >
        {/* Inner absolute div is the positioning context for VibesitesImage */}
        <div className="absolute inset-0">
          <EditableSlotGroup groupPath="pinnedcallouts.bg_image" definition={bgImageDef}>
            {bg.image ? (
              <VibesitesImage
                url={bg.image}
                alt={bg.alt || ''}
                sizes="100vw"
                className="absolute inset-0 w-full h-full object-cover object-center"
              />
            ) : (
              <div className="absolute inset-0 bg-[var(--vibe-color-primary)]" />
            )}
          </EditableSlotGroup>
        </div>
      </div>

      {/* ── Dark overlay (z-[1], above bg, below content) ── */}
      <div className="absolute inset-0 bg-black/55 z-[1]" />

      {/* ── Content (z-10) ── */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={contentRef} className="reveal-fade-up max-w-2xl mx-auto text-center md:mx-0 md:text-left py-14 md:py-16">
          {/* Subtitle — the active special offer's title, else the editable
              generic subtitle token. */}
          {offerTitle ? (
            <p className="font-heading italic text-xl sm:text-2xl text-white/90 mb-3">
              {offerTitle}
            </p>
          ) : (
            <RichTextContent
              path="pinnedcallouts.subtitle"
              label="Subtitle"
              defaultValue='<p>Thinking about calling <span data-type="field-token" data-field="property_name"></span> your new home?</p>'
              as="p"
              className="font-heading italic text-xl sm:text-2xl text-white/90 mb-3"
            />
          )}

          {/* Title — a section heading that flips copy based on whether an
              offer is running. Both variants stay editable. */}
          {hasActiveOffer ? (
            <RichTextContent
              path="pinnedcallouts.title"
              label="Offer Title"
              defaultValue="<p>Enjoy Our Special Offer</p>"
              as="h2"
              className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-[1.05] uppercase tracking-wide mb-5"
            />
          ) : (
            <RichTextContent
              path="pinnedcallouts.title_fallback"
              label="Fallback Title"
              defaultValue="<p>Schedule a Tour Today</p>"
              as="h2"
              className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-[var(--vibe-color-primary)] leading-[1.05] uppercase tracking-wide mb-5"
              style={fallbackTitleColor ? { color: fallbackTitleColor } : undefined}
            />
          )}

          {/* Body — the active special offer's description, else the editable
              generic body token. */}
          {offerDescription ? (
            <p className="font-body text-sm sm:text-base text-white/85 leading-relaxed mb-8 max-w-md mx-auto md:mx-0">
              {offerDescription}
            </p>
          ) : (
            <RichTextContent
              path="pinnedcallouts.body"
              label="Body"
              defaultValue="<p>Fill out the contact form and be the first to know about our special offers and availability!</p>"
              as="p"
              className="font-body text-sm sm:text-base text-white/85 leading-relaxed mb-8 max-w-md mx-auto md:mx-0"
            />
          )}

          {/* CTA */}
          <EditableSlotGroup groupPath="pinnedcallouts.cta" definition={ctaDef}>
            <a
              href={cta.url || '/contact'}
              target={cta._target || '_self'}
              className="pinnedcallouts-cta relative inline-flex items-center gap-2.5 px-10 py-4 font-body text-sm font-semibold tracking-[0.2em] uppercase overflow-hidden transition-all duration-300"
            >
              <span className="relative z-10">{cta.label || 'Contact Us'}</span>
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        /* Solid-white CTA — white fill, dark label. On hover it fills with the
           theme primary and flips the label to text-on-primary. */
        .pinnedcallouts-cta {
          border-radius: 2px;
          background: #fff;
          color: var(--vibe-color-text, #1a1a1a);
          box-shadow: none;
        }
        .pinnedcallouts-cta::before {
          content: '';
          position: absolute;
          inset: 0;
          background: var(--vibe-color-primary);
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        .pinnedcallouts-cta:hover {
          color: var(--vibe-color-text-on-primary, #fff);
          transform: translateY(-2px);
          box-shadow: 0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }
        .pinnedcallouts-cta:hover::before {
          opacity: 1;
        }
        .pinnedcallouts-cta:active {
          transform: translateY(0);
          box-shadow: none;
        }
      `}</style>
    </section>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'PinnedCallouts',
  displayName: 'Pinned Callouts',
  tokens: {
    'pinnedcallouts.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue:
        '<p>Thinking about calling <span data-type="field-token" data-field="property_name"></span> your new home?</p>',
    },
    'pinnedcallouts.title': {
      label: 'Offer Title',
      type: 'richtext',
      defaultValue: '<p>Enjoy Our Special Offer</p>',
    },
    'pinnedcallouts.title_fallback': {
      label: 'Fallback Title',
      type: 'richtext',
      defaultValue: '<p>Schedule a Tour Today</p>',
    },
    'pinnedcallouts.body': {
      label: 'Body',
      type: 'richtext',
      defaultValue:
        '<p>Fill out the contact form and be the first to know about our special offers and availability!</p>',
    },
  },
  slotGroups: {
    'pinnedcallouts.bg_image': {
      label: 'Background Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 2,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
    'pinnedcallouts.cta': {
      label: 'CTA',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/contact' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Contact Us' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
  collectionBindings: {
    'pinnedcallouts.offers': {
      label: 'Special Offers',
      collection: 'special_offers',
    },
  },
};

(PinnedCallouts as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PinnedCallouts;
